"use client";

import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "../lib/utils";
import { Icon, IconComponent, IconKey } from "./icon";

const badgeVariants = cva(
  "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive inline-flex w-fit min-w-6 shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-medium transition-[color] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 [&_svg]:pointer-events-none [&_svg]:size-3.5",

  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground",
        secondary: "border-transparent bg-secondary text-secondary-foreground",
        outline: "text-foreground",
        // Tinted from the existing `destructive` token (already used by
        // Button, and never shared with the error Alert/Toast surfaces,
        // which key off `ui-danger` instead) rather than a dedicated
        // background var — avoids a one-off CSS variable.
        destructive:
          "border-transparent bg-destructive/15 text-secondary-foreground",
        success:
          "border-transparent bg-ui-success-background text-secondary-foreground",
        warning:
          "border-transparent bg-ui-warning-background text-secondary-foreground",
        info: "border-transparent bg-ui-info-background text-secondary-foreground",
      },
      size: {
        default: "",
        md: "gap-1 px-3 py-1 text-sm [&_svg]:size-4",
      },
      rounded: {
        true: "rounded-full px-1 py-0",
      },
      overrideClass: {
        true: "ui-badge",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

/** Icon shown by default for each semantic variant. */
const variantIcons: Partial<
  Record<NonNullable<BadgeProps["variant"]>, IconKey>
> = {
  success: "success",
  destructive: "destructive",
  warning: "warning",
  info: "info",
};

type BadgeProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof badgeVariants> & {
    /**
     * Overrides the variant's default icon. Pass a custom icon element to replace
     * it, or `null` to disable the default icon entirely. Leave undefined to keep
     * the variant's default.
     */
    icon?: IconComponent | null;
    extended?: boolean;
  };

/**
 * shadcn/ui docs: https://ui.shadcn.com/docs/components/badge
 */
const Badge = ({
  className,
  children,
  icon,

  variant,
  size,
  rounded,
  overrideClass = true,

  extended = false,
  ...props
}: BadgeProps) => {
  const defaultIcon = variant ? variantIcons[variant] : undefined;
  const resolvedIcon =
    icon === undefined
      ? defaultIcon && <Icon icon={defaultIcon} weight="fill" />
      : icon;

  return (
    <div
      className={
        extended
          ? className
          : cn(
              badgeVariants({ variant, size, rounded, overrideClass }),
              className,
            )
      }
      data-variant={variant}
      {...props}
    >
      {resolvedIcon}
      {children}
    </div>
  );
};

export { Badge, badgeVariants, type BadgeProps };
